import { DeckspaceError } from '@deckspace/errors';
import type { PageId, SurfaceId, WorkspaceId } from '@deckspace/ids';
import type { DeckspaceErrorCode } from '@deckspace/types';
import type {
  DatasetRecord,
  DatasetQueryConfig,
  ColumnProfile,
  Surface,
  SurfaceCreateInput,
  SurfaceUpdate,
  Page,
  WorkspaceDetail,
  WorkspaceListItem,
} from '@deckspace/types';

export interface ApiClient {
  readonly baseUrl: string;

  // Auth
  getSession(): Promise<{ user: { id: string; email: string } | null }>;
  getLoginUrl(redirect?: string): Promise<string>;
  logout(): Promise<void>;

  // Workspaces
  listWorkspaces(): Promise<Array<{ id: WorkspaceId; name: string | null; createdAt: string }>>;
  listWorkspacesWithSummary(): Promise<WorkspaceListItem[]>;
  createWorkspace(
    name: string,
    metadata?: Record<string, unknown>
  ): Promise<{ id: WorkspaceId; name: string }>;
  workspaceExists(id: WorkspaceId): Promise<boolean>;
  renameWorkspace(id: WorkspaceId, name: string): Promise<{ id: WorkspaceId; name: string }>;
  updateWorkspaceMetadata(
    id: WorkspaceId,
    metadata: Record<string, unknown>
  ): Promise<{ id: WorkspaceId; metadata: Record<string, unknown> }>;
  deleteWorkspace(id: WorkspaceId): Promise<void>;
  getWorkspaceDetail(id: WorkspaceId): Promise<WorkspaceDetail>;

  // Pages
  listPages(workspaceId: WorkspaceId): Promise<Page[]>;
  createPage(
    workspaceId: WorkspaceId,
    name: string,
    sortOrder?: number
  ): Promise<{ id: PageId; name: string; sortOrder: number }>;
  getPage(id: PageId): Promise<Page>;
  renamePage(
    id: PageId,
    name: string
  ): Promise<{ workspaceId: WorkspaceId; id: PageId; name: string }>;
  reorderPages(workspaceId: WorkspaceId, order: PageId[]): Promise<{ reordered: boolean }>;
  deletePage(id: PageId): Promise<{ workspaceId: WorkspaceId; id: PageId }>;

  // Surfaces
  listSurfaces(workspaceId: WorkspaceId): Promise<Surface[]>;
  createSurface(input: SurfaceCreateInput): Promise<Surface>;
  getSurface(id: SurfaceId): Promise<Surface>;
  getSurfaceByPageId(pageId: PageId): Promise<Surface>;
  updateSurface(id: SurfaceId, updates: SurfaceUpdate): Promise<Surface>;
  deleteSurface(id: SurfaceId): Promise<{ workspaceId: WorkspaceId; id: SurfaceId }>;

  // Datasets
  listDatasets(workspaceId: WorkspaceId): Promise<DatasetRecord[]>;
  getDataset(workspaceId: WorkspaceId, name: string): Promise<DatasetRecord>;
  queryDataset(
    workspaceId: WorkspaceId,
    name: string,
    queryConfig?: DatasetQueryConfig
  ): Promise<Record<string, unknown>[]>;
  updateDatasetProfiles(
    workspaceId: WorkspaceId,
    name: string,
    profiles: ColumnProfile[]
  ): Promise<void>;
  findSurfacesReferencingDataset(
    workspaceId: WorkspaceId,
    name: string
  ): Promise<Array<{ surfaceId: SurfaceId; pageId: PageId; pageName: string }>>;
}

/**
 * Create a typed API client for the Deckspace REST API.
 * All requests use credentials: 'include' for cookie-based auth.
 * On 401, the client attempts a single token refresh (POST /api/auth/refresh).
 * If the refresh succeeds (204), the original request is retried once.
 * If the refresh fails, a `deckspace:unauthorized` window event is dispatched and a
 * DeckspaceError with code UNAUTHORIZED is thrown.
 * Concurrent refresh attempts are deduplicated via a shared in-flight promise.
 */
export function createApiClient(baseUrl: string): ApiClient {
  let inFlightRefresh: Promise<boolean> | null = null;

  function tryRefresh(): Promise<boolean> {
    if (inFlightRefresh) return inFlightRefresh;
    inFlightRefresh = fetch(`${baseUrl}/api/auth/refresh`, {
      method: 'POST',
      credentials: 'include',
    })
      .then((res) => res.ok)
      .catch(() => false)
      .finally(() => {
        inFlightRefresh = null;
      });
    return inFlightRefresh;
  }

  async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
    const doFetch = () =>
      fetch(`${baseUrl}${path}`, {
        method,
        credentials: 'include',
        headers: body !== undefined ? { 'Content-Type': 'application/json' } : undefined,
        body: body !== undefined ? JSON.stringify(body) : undefined,
      });

    let res = await doFetch();

    if (res.status === 401 && path !== '/api/auth/refresh') {
      const refreshed = await tryRefresh();
      if (refreshed) {
        res = await doFetch();
      }
    }

    if (res.status === 401 && typeof window !== 'undefined') {
      window.dispatchEvent(new Event('deckspace:unauthorized'));
    }

    if (res.status === 204) return undefined as T;

    // Attempt to parse response body; fall back gracefully on empty/null bodies.
    let data: unknown;
    try {
      data = await res.json();
    } catch {
      if (!res.ok) {
        if (res.status === 401) {
          throw new DeckspaceError('UNAUTHORIZED', `HTTP ${res.status}`);
        }
        throw new Error(`HTTP ${res.status}`);
      }
      return undefined as T;
    }

    if (!res.ok) {
      if (data && typeof data === 'object' && 'code' in data && 'message' in data) {
        const errData = data as {
          code: DeckspaceErrorCode;
          message: string;
          details?: Record<string, unknown>;
        };
        throw new DeckspaceError(errData.code, errData.message, errData.details);
      }
      throw new Error((data as { message?: string } | null)?.message ?? `HTTP ${res.status}`);
    }

    return data as T;
  }

  const get = <T>(path: string) => request<T>('GET', path);
  const post = <T>(path: string, body?: unknown) => request<T>('POST', path, body);
  const patch = <T>(path: string, body: unknown) => request<T>('PATCH', path, body);
  const put = <T>(path: string, body?: unknown) => request<T>('PUT', path, body);
  const del = <T>(path: string, body?: unknown) => request<T>('DELETE', path, body);

  return {
    baseUrl,

    // Auth
    getSession: () => get('/api/auth/session'),
    getLoginUrl: async (redirect = '/') => {
      const data = await get<{ url: string }>(
        `/api/auth/login?redirect=${encodeURIComponent(redirect)}`
      );
      return data.url;
    },
    logout: () => post('/api/auth/logout'),

    // Workspaces
    listWorkspaces: () => get('/api/workspaces'),
    listWorkspacesWithSummary: () => get('/api/workspaces/summary'),
    createWorkspace: (name, metadata) => post('/api/workspaces', { name, metadata }),
    workspaceExists: async (id) => {
      const data = await get<{ exists: boolean }>(`/api/workspaces/${id}/exists`);
      return data.exists;
    },
    renameWorkspace: (id, name) => patch(`/api/workspaces/${id}/name`, { name }),
    updateWorkspaceMetadata: (id, metadata) => patch(`/api/workspaces/${id}/metadata`, metadata),
    deleteWorkspace: (id) => del(`/api/workspaces/${id}`),
    getWorkspaceDetail: (id) => get(`/api/workspaces/${id}/detail`),

    // Pages
    listPages: (wsId) => get(`/api/workspaces/${wsId}/pages`),
    createPage: (wsId, name, sortOrder) =>
      post(`/api/workspaces/${wsId}/pages`, { name, sortOrder }),
    getPage: (id) => get(`/api/pages/${id}`),
    renamePage: (id, name) => patch(`/api/pages/${id}/name`, { name }),
    reorderPages: (wsId, order) => put(`/api/workspaces/${wsId}/pages/order`, { order }),
    deletePage: (id) => del(`/api/pages/${id}`),

    // Surfaces
    listSurfaces: (wsId) => get(`/api/workspaces/${wsId}/surfaces`),
    createSurface: (input) => post('/api/surfaces', input),
    getSurface: (id) => get(`/api/surfaces/${id}`),
    getSurfaceByPageId: (pageId) => get(`/api/pages/${pageId}/surface`),
    updateSurface: (id, updates) => patch(`/api/surfaces/${id}`, updates),
    deleteSurface: (id) => del(`/api/surfaces/${id}`),

    // Datasets
    listDatasets: (wsId) => get(`/api/workspaces/${wsId}/datasets`),
    getDataset: (wsId, name) => get(`/api/workspaces/${wsId}/datasets/${name}`),
    queryDataset: (wsId, name, queryConfig) =>
      post(`/api/workspaces/${wsId}/datasets/${name}/query`, queryConfig ?? {}),
    updateDatasetProfiles: (wsId, name, profiles) =>
      post<void>(`/api/workspaces/${wsId}/datasets/${name}/profiles`, { profiles }),
    findSurfacesReferencingDataset: (wsId, name) =>
      get(`/api/workspaces/${wsId}/datasets/${name}/surfaces`),
  };
}
